import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';

function log(label: string, data?: unknown) {
  // eslint-disable-next-line no-console
  console.log(`[useClienteCalendario] ${label}`, data ?? '');
}

export interface LookCalendario {
  id: number;
  cliente_id: number;
  fecha: string;
  titulo: string;
  notas: string | null;
  prendas_ids: number[];
  ocasion: string | null;
  estacion: string | null;
  estado: 'planeado' | 'usado' | 'cancelado';
  foto_real_url?: string | null;
  calificacion?: number | null;
  comentario_cliente?: string | null;
}

export function useClienteCalendario(initialData: LookCalendario[], clienteId?: number) {
  const [data, setData] = useState<LookCalendario[]>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastClienteIdRef = useRef<number | undefined>(undefined);
  const isFirstMount = useRef(true);

  // On first mount or when clienteId changes: silently refetch in background
  // Keep existing data visible to avoid flash; update when response arrives
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      lastClienteIdRef.current = clienteId;
      setLoading(true);
      setData(initialData);
      return;
    }
    if (lastClienteIdRef.current !== clienteId) {
      lastClienteIdRef.current = clienteId;
      if (initialData.length > 0) {
        setData(initialData);
      }
      setError(null);
    }
  }, [initialData, clienteId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let query = supabase
          .from('cliente_calendario_looks')
          .select('*')
          .order('fecha', { ascending: true });
        if (clienteId !== undefined && clienteId > 0) {
          query = query.eq('cliente_id', clienteId);
        }
        const { data: rows, error: err } = await query;
        if (err) {
          setError(err.message);
        } else if (rows && rows.length > 0) {
          setData(rows as LookCalendario[]);
        } else if (initialData.length > 0) {
          setData(initialData);
        }
      } catch {
        setError('Error de conexión. Usando datos locales.');
        if (initialData.length > 0) setData(initialData);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const add = useCallback(
    async (item: Omit<LookCalendario, 'id'>) => {
      setLoading(true);
      setError(null);
      try {
        const payload = { ...item, cliente_id: clienteId ?? item.cliente_id };
        log('add payload', payload);
        const { error: err } = await supabase
          .from('cliente_calendario_looks')
          .insert(payload);
        if (err) {
          log('add error', err);
          setError(err.message);
          throw new Error(err.message);
        }
        log('add success, refreshing...');
        await refresh();
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Error al crear';
        log('add catch', msg);
        setError(msg);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [clienteId]
  );

  const update = useCallback(
    async (id: number, updates: Partial<LookCalendario>) => {
      setLoading(true);
      setError(null);
      try {
        log('update', { id, updates });
        let query = supabase
          .from('cliente_calendario_looks')
          .update(updates)
          .eq('id', id);
        if (clienteId !== undefined && clienteId > 0) {
          query = query.eq('cliente_id', clienteId);
        }
        const { error: err } = await query;
        if (err) {
          log('update error', err);
          setError(err.message);
          throw new Error(err.message);
        }
        log('update success, refreshing...');
        await refresh();
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'Error al actualizar';
        log('update catch', msg);
        setError(msg);
        throw e;
      } finally {
        setLoading(false);
      }
    },
    [clienteId]
  );

  const remove = useCallback(async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      log('remove', { id });
      let query = supabase
        .from('cliente_calendario_looks')
        .delete()
        .eq('id', id);
      if (clienteId !== undefined && clienteId > 0) {
        query = query.eq('cliente_id', clienteId);
      }
      const { error: err } = await query;
      if (err) {
        log('remove error', err);
        setError(err.message);
        throw new Error(err.message);
      }
      log('remove success, refreshing...');
      await refresh();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Error al eliminar';
      log('remove catch', msg);
      setError(msg);
      throw e;
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      log('refresh');
      let query = supabase
        .from('cliente_calendario_looks')
        .select('*')
        .order('fecha', { ascending: true });
      if (clienteId !== undefined && clienteId > 0) {
        query = query.eq('cliente_id', clienteId);
      }
      const { data: rows, error: err } = await query;
      if (err) {
        log('refresh error', err);
        setError(err.message);
      } else {
        log('refresh rows', rows?.length ?? 0);
        setData((rows as LookCalendario[]) ?? []);
      }
    } catch (e) {
      log('refresh catch', e);
      setError('Error de conexión');
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  return { data, setData, add, update, remove, refresh, loading, error };
}
